import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Image,
  Input,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import publicVendorApi from "../services/publicVendorApi";

const initialFormData = {
  name: "",
  email: "",
  phone: "",
  gstNumber: "",
  siteLocation: "",
  street: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
};

const VendorSelfRegister = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";

  const [loadingInvite, setLoadingInvite] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteInfo, setInviteInfo] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    const loadInvite = async () => {
      if (!inviteToken) {
        setInviteError(
          "Missing invite token. Please use the shared registration link.",
        );
        setLoadingInvite(false);
        return;
      }

      try {
        setLoadingInvite(true);
        const response = await publicVendorApi.getInvitePreview(inviteToken);
        const data = response?.data || null;
        setInviteInfo(data);
        setFormData((prev) => ({
          ...prev,
          email: data?.prefillEmail || prev.email,
          siteLocation: data?.presetSiteLocation || prev.siteLocation,
        }));
      } catch (error) {
        setInviteError(
          error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "This invite link is invalid or expired.",
        );
      } finally {
        setLoadingInvite(false);
      }
    };

    loadInvite();
  }, [inviteToken]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      await publicVendorApi.selfRegisterVendor({
        inviteToken,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        gstNumber: formData.gstNumber,
        siteLocation: formData.siteLocation,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          postalCode: formData.postalCode,
        },
      });

      setSubmitted(true);
      toast({
        title: "Registration submitted",
        description: "Vendor registration completed successfully.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Registration failed",
        description:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to submit vendor registration.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxW="3xl" py={{ base: 4, md: 10 }} px={{ base: 3, md: 6 }}>
      <Box
        borderWidth="1px"
        borderRadius={{ base: "lg", md: "xl" }}
        p={{ base: 4, md: 8 }}
        bg="white"
        boxShadow={{ base: "none", md: "sm" }}
      >
        <Stack
          direction={{ base: "column", sm: "row" }}
          align={{ base: "flex-start", sm: "center" }}
          spacing={4}
          mb={6}
        >
          {inviteInfo?.companyLogo ? (
            <Image
              src={inviteInfo.companyLogo}
              alt={`${inviteInfo?.companyName || "Company"} logo`}
              objectFit="contain"
              maxH={{ base: "56px", md: "64px" }}
              maxW={{ base: "140px", md: "180px" }}
              borderRadius="md"
              borderWidth="1px"
              p={2}
              bg="white"
            />
          ) : null}

          <Box>
            <Heading size={{ base: "md", md: "lg" }} mb={1}>
              Vendor Self Registration
            </Heading>
            <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>
              Complete this form to onboard as a vendor.
            </Text>
          </Box>
        </Stack>

        {loadingInvite && (
          <Stack align="center" py={8}>
            <Spinner size="lg" />
            <Text color="gray.500">Validating invite link...</Text>
          </Stack>
        )}

        {!loadingInvite && inviteError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Invite link error</AlertTitle>
              <AlertDescription>{inviteError}</AlertDescription>
            </Box>
          </Alert>
        )}

        {!loadingInvite && !inviteError && submitted && (
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Registration successful</AlertTitle>
              <AlertDescription>
                Your vendor profile has been submitted for the organization
                {inviteInfo?.companyName ? ` ${inviteInfo.companyName}` : ""}.
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {!loadingInvite && !inviteError && !submitted && (
          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {inviteInfo?.companyName && (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Organization</AlertTitle>
                    <AlertDescription>
                      {inviteInfo.companyName}
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              <FormControl isRequired>
                <FormLabel>Vendor Name</FormLabel>
                <Input value={formData.name} onChange={handleChange("name")} />
              </FormControl>

              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={handleChange("email")}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Phone</FormLabel>
                    <Input
                      value={formData.phone}
                      onChange={handleChange("phone")}
                    />
                  </FormControl>
                </GridItem>
              </Grid>

              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                <GridItem>
                  <FormControl>
                    <FormLabel>GST Number</FormLabel>
                    <Input
                      value={formData.gstNumber}
                      onChange={handleChange("gstNumber")}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Site Location</FormLabel>
                    <Input
                      value={formData.siteLocation}
                      onChange={handleChange("siteLocation")}
                    />
                  </FormControl>
                </GridItem>
              </Grid>

              <FormControl>
                <FormLabel>Street Address</FormLabel>
                <Input
                  value={formData.street}
                  onChange={handleChange("street")}
                />
              </FormControl>

              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                <GridItem>
                  <FormControl>
                    <FormLabel>City</FormLabel>
                    <Input
                      value={formData.city}
                      onChange={handleChange("city")}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>State</FormLabel>
                    <Input
                      value={formData.state}
                      onChange={handleChange("state")}
                    />
                  </FormControl>
                </GridItem>
              </Grid>

              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                <GridItem>
                  <FormControl>
                    <FormLabel>Country</FormLabel>
                    <Input
                      value={formData.country}
                      onChange={handleChange("country")}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Postal Code</FormLabel>
                    <Input
                      value={formData.postalCode}
                      onChange={handleChange("postalCode")}
                    />
                  </FormControl>
                </GridItem>
              </Grid>

              <Button
                colorScheme="blue"
                type="submit"
                isLoading={submitting}
                loadingText="Submitting"
                w={{ base: "full", md: "auto" }}
              >
                Submit Registration
              </Button>
            </Stack>
          </form>
        )}
      </Box>
    </Container>
  );
};

export default VendorSelfRegister;
